import { createContext, useCallback, useContext, useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import { api } from '../api/client';

type Me = { authenticated: boolean; username?: string; is_staff?: boolean };

type AuthValue = {
  user: Me | null;
  loading: boolean;
  login: (username: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
};

const AuthContext = createContext<AuthValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<Me | null>(null);
  const [loading, setLoading] = useState(true);

  // Also primes the CSRF cookie the write endpoints need.
  const refresh = useCallback(async () => {
    try {
      setUser(await api<Me>('/auth/me/'));
    } catch {
      // Offline on first load: fall back to unauthenticated rather than
      // wedging the app on a spinner.
      setUser({ authenticated: false });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const login = useCallback(async (username: string, password: string) => {
    const result = await api<{ username: string; is_staff: boolean }>('/auth/login/', {
      method: 'POST',
      body: { username, password },
    });
    setUser({ authenticated: true, ...result });
  }, []);

  const logout = useCallback(async () => {
    await api<void>('/auth/logout/', { method: 'POST' });
    setUser({ authenticated: false });
  }, []);

  return (
    <AuthContext.Provider value={{ user, loading, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const value = useContext(AuthContext);
  if (!value) throw new Error('useAuth must be used inside AuthProvider');
  return value;
}
